//
// UTILITIES
// Description: List of commonly used or reusable Methods
// Note       : Sort your Method by Name Ascending
//              Start your Method's name by Capital Letter
//

import {MYR} from '@dinero.js/currencies'
import {UserModel} from 'app/modules/auth'
import {Currency, add, dinero, subtract, toDecimal, toSnapshot} from 'dinero.js'
import FileSaver from 'file-saver'
import {FormikProps} from 'formik'
import {useEffect, useRef} from 'react'
import {Group, Role} from './Enums'
import {toPng, toSvg} from 'html-to-image'

// Converts a base64string to blob https://stackoverflow.com/a/20151856
export const ConvertBase64toBlob = (base64Data: string, contentType: string) => {
  contentType = contentType || ''
  var sliceSize = 1024
  var byteCharacters = atob(base64Data)
  var bytesLength = byteCharacters.length
  var slicesCount = Math.ceil(bytesLength / sliceSize)
  var byteArrays = new Array(slicesCount)

  for (var sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    var begin = sliceIndex * sliceSize
    var end = Math.min(begin + sliceSize, bytesLength)

    var bytes = new Array(end - begin)
    for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0)
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes)
  }
  return new Blob(byteArrays, {type: contentType})
}

export const ConvertDataStorage = (from: 'Bytes' | 'KB' | 'MB' | null, to: 'Bytes' | 'KB' | 'MB' | null, value?: number | null) => {
  if (value == null || value == undefined) return undefined

  let units = ['Bytes', 'KB', 'MB']
  let conversionRate = 1024
  let fromIndex = units.findIndex((x) => x == from)
  let toIndex = units.findIndex((x) => x == to)
  let difference = fromIndex - toIndex

  if (difference == 0) {
    return value
  } else if (Math.sign(difference) == 1) {
    return value * Math.pow(conversionRate, Math.abs(difference))
  } else if (Math.sign(difference) == -1) {
    return value / Math.pow(conversionRate, Math.abs(difference))
  } else {
    return undefined
  }
}

export const ConvertDateFormat = (date: Date | null | undefined) => {
  if (!date) return date

  date = new Date(date)
  date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))

  return date
}

export const ConvertDateTimeFormat = (date: Date | null | undefined) => {
  if (!date) return date

  date = new Date(date)
  date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes())

  return date
}

export const ConvertNumberToMoney = (unit: string, value?: number, useIntFormatter?: boolean, decimalPlace: number = 2) => {
  if (value == undefined) return `${unit} ${DashIfEmpty(undefined)}`.trim()
  if (useIntFormatter) return `${unit} ${IntFormatter(value)}`.trim()
  else return `${unit} ${value.toFixed(decimalPlace).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`.trim()
}

export const ConvertStringToBoolean = (value: string) => {
  if (!Boolean(value)) return false
  else return value.toLowerCase() === 'true' ? true : false
}

export const DashIfEmpty = (text?: string | null) => {
  if (StringIsNullOrEmpty(text)) return '-'
  return text as string
}

export const DownloadBlob = (response: any) => {
  const fileArray = response.data
  const fileName = response.headers['content-disposition'].split('filename=')[1].split(';')[0]
  const fileType = response.headers['content-type']

  FileSaver.saveAs(new Blob([fileArray], {type: fileType}), fileName.replaceAll('"', ''))
}

export const GetDateString = (date: string = new Date().toISOString()) => {
  return date.split('T')[0]
}

export const GetExtensionIconPath = (type: string | undefined) => {
  switch (type) {
    case '.doc':
    case '.docx':
      return `/media/extensions/doc.png`
    case '.jpg':
    case '.jpeg':
      return `/media/extensions/jpg.png`
    case '.gif':
    case '.giff':
      return `/media/extensions/gif.png`
    case '.png':
      return `/media/extensions/png.png`
    case '.pdf':
      return `/media/extensions/pdf.png`
    case '.ppt':
      return `/media/extensions/ppt.png`
    case '.txt':
      return `/media/extensions/txt.png`
    case '.xls':
    case '.xlsx':
      return `/media/extensions/xls.png`
    case '.zip':
      return `/media/extensions/zip.png`
    default:
      return `/media/extensions/documents.png`
  }
}

export const GetFormikValidClass = (formik: FormikProps<any>, fieldKey: string) => {
  if (formik.touched[fieldKey] && formik.errors[fieldKey]) return 'is-invalid'
  if (formik.touched[fieldKey] && !formik.errors[fieldKey]) return 'is-valid'
  return ''
}

export const GetIconName = (fileName: string) => {
  var extension = fileName.split('.').pop()

  switch (extension) {
    case 'docx':
      return 'doc'
    case 'xlsx':
      return 'xls'
    case 'pptx':
      return 'ppt'
    case 'jpeg':
      return 'jpg'
    default:
      return extension
  }
}

export const GetInitials = (name: string) => {
  var allNames = name.trim().split(' ')

  var initials = allNames.reduce((acc, curr, index) => {
    if (index === 0 || index === allNames.length - 1) {
      acc = `${acc}${curr.charAt(0).toUpperCase()}`
    }

    return acc
  }, '')

  return initials
}

export const GetPaginationLinks = (currentPage: number, totalPages: number | undefined, maximumPagination: number) => {
  var links: any[] = []
  if (!totalPages || totalPages < 1) return links

  var oddMaximumPagination = maximumPagination % 2 == 0 ? maximumPagination - 1 : maximumPagination
  var availableSlots = oddMaximumPagination
  var slotsInHalf = Math.floor(oddMaximumPagination / 2)
  var isCurrentPageInFirstHalf = currentPage <= totalPages / 2

  if (isCurrentPageInFirstHalf) {
    for (var i = currentPage; i > 0; i--) {
      links.unshift({label: `${i}`, page: i})
      availableSlots -= 1

      if (availableSlots == slotsInHalf) {
        if (currentPage - slotsInHalf > 1) {
          if (i != 1 && i - 1 != 1) links.unshift({label: 'Separator-Left', page: null})
          if (i != 1) links.unshift({label: '1', page: 1})
        }
        break
      }
    }
    links.unshift({label: 'Previous', page: currentPage - 1})

    for (var i = currentPage + 1; i <= totalPages; i++) {
      links.push({label: `${i}`, page: i})
      availableSlots -= 1

      if (availableSlots == 0) {
        if (currentPage + slotsInHalf < totalPages) {
          if (i != totalPages && i + 1 != totalPages) links.push({label: 'Separator-Right', page: null})
          if (i != totalPages) links.push({label: totalPages, page: totalPages})
        }
        break
      }
    }
    links.push({label: 'Next', page: currentPage + 1})
  } else {
    for (var i = currentPage; i <= totalPages; i++) {
      links.push({label: `${i}`, page: i})
      availableSlots -= 1

      if (availableSlots == slotsInHalf) {
        if (currentPage + slotsInHalf < totalPages) {
          if (i != totalPages && i + 1 != totalPages) links.push({label: 'Separator-Right', page: null})
          if (i != totalPages) links.push({label: totalPages, page: totalPages})
        }
        break
      }
    }
    links.push({label: 'Next', page: currentPage + 1})

    for (var i = currentPage - 1; i > 0; i--) {
      links.unshift({label: `${i}`, page: i})
      availableSlots -= 1

      if (availableSlots == 0) {
        if (currentPage - slotsInHalf > 1) {
          if (i != 1 && i - 1 != 1) links.unshift({label: 'Separator-Left', page: null})
          if (i != 1) links.unshift({label: '1', page: 1})
        }
        break
      }
    }
    links.unshift({label: 'Previous', page: currentPage - 1})
  }

  return links
}

export const GetParameterByName = function (name: string, url?: string) {
  if (!url) url = window.location.href
  url = url.toLowerCase()
  name = name.replace(/[\[\]]/g, '\\$&')
  var regex = new RegExp('[?&]' + name.toLowerCase() + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url)
  if (!results) return null
  if (!results[2]) return ''
  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

export const HasPermissionInGroup = (currentUser: UserModel | undefined, groupsToCheck: Group[]) => {
  return currentUser?.groups?.some((item) => groupsToCheck.includes(item)) ?? false
}
export const HasPermissionInRole = (currentUser: UserModel | undefined, rolesToCheck: Role[]) => {
  return currentUser?.roles?.some((item) => rolesToCheck.includes(item)) ?? false
}

export const IntFormatter = (value: number, decimalPlace?: number) => {
  // this function is use for convert number to string with abbreviation symbol eg 1000 -> 1k
  // will round up to integer if no dp is provided
  const lookup = [
    {value: 1, symbol: ''},
    {value: 1e3, symbol: 'K'},
    {value: 1e6, symbol: 'M'},
    {value: 1e9, symbol: 'B'},
    {value: 1e12, symbol: 'T'},
    {value: 1e15, symbol: 'Q'},
    {value: 1e18, symbol: 'Qi'},
  ]
  var item = [...lookup].reverse().find((item) => value >= item.value)
  return item ? (value / item.value).toFixed(decimalPlace) + item.symbol : '0'
}

export const IsFilesFormatValid = (files: File | File[], MIMEformat: string) => {
  const formatArray = MIMEformat.split(',')

  if (Symbol.iterator in Object(files)) {
    if (
      Array.from(files as File[]).some((file) => {
        return !formatArray.includes(file.type)
      })
    )
      return false
  } else {
    if (!formatArray.includes((files as File).type)) return false
  }

  return true
}

export const IsFilesSizeValid = (files: File | File[], sizeInBytes: number) => {
  if (Symbol.iterator in Object(files)) {
    if (
      Array.from(files as File[]).some((file) => {
        return file.size > sizeInBytes
      })
    )
      return false
  } else {
    if ((files as File).size > sizeInBytes) return false
  }

  return true
}

export const IsPromise = (value: any) => {
  return Boolean(value && typeof value.then === 'function')
}

export const ObjectIsEmpty = (obj: Object) => {
  if (typeof obj === 'object') {
    for (let prop in obj) {
      if (Object.hasOwn(obj, prop)) {
        return false
      }
    }
  }
  return true
}

export const RemoveAllSubstringsFromString = (value: string, substrings: string[]) => {
  for (let substring of substrings) {
    value = value.replaceAll(substring, '')
  }
  return value
}

export const RoundedDownNumber = (value: number, decimalPlace: number = 2) => {
  return Math.floor(value * Math.pow(10, decimalPlace)) / Math.pow(10, decimalPlace)
}

export const RoundedHalf = (value: number) => {
  // round to nearest 0.5 eg. 1.2 -> 1.0, 1.3 -> 1.5, 1.6 -> 1.5, 1.8 -> 2.0
  return Math.round(value / 0.5) * 0.5
}

export const StringIsNullOrEmpty = (text?: string | null) => {
  return text == null || text == ''
}

export const UsePrevious = (value: any) => {
  const ref = useRef()
  useEffect(() => {
    ref.current = value //assign the value of ref to the argument
  }, [value]) //this code will run when the value of 'value' changes
  return ref.current //in the end, return the current ref value.
}

export const roundToDecimalPlaces = (value: number, decimalPlaces: number): number => {
  // Custom function to round a number to specified decimal places

  const multiplier = Math.pow(10, decimalPlaces)
  return Math.round(value * multiplier) / multiplier
}

//#region HTML-To-Image
// Custom function to export html as image files
// Allow custom css
export const capturePng = async (element: HTMLElement) => {
  const dataUrl = await toPng(element)
  return dataUrl
}

export const captureSvg = async (element: HTMLElement) => {
  const dataUrl = await toSvg(element)
  return dataUrl
}

export const downloadImage = (dataUrl: string, filename?: string, format: string = 'png') => {
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = filename || `image-${new Date().toISOString().split('T')[0]}.${format}`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
//#endregion

//#region Dinero
export const DineroAdd = (values: number[], selectedCurrency: Currency<number> = MYR) => {
  let total = dinero({amount: 0, currency: selectedCurrency})
  const factor = 10 ** toSnapshot(total).currency.exponent

  for (let value of values) {
    // convert amount to integer, Dinerojs represent money in smallest subdivision of the currency i.e. cents
    total = add(total, dinero({amount: Math.round(value * factor), currency: selectedCurrency}))
  }

  return toDecimal(total, ({value}) => Number(value))
}

export const DineroSubtract = (values: number[], selectedCurrency: Currency<number> = MYR) => {
  if (values.length > 0) {
    const factor = 10 ** toSnapshot(dinero({amount: 0, currency: selectedCurrency})).currency.exponent
    let total = dinero({amount: Math.round(values[0] * factor), currency: selectedCurrency})

    for (let i = 1; i < values.length; i++) {
      // convert amount to integer, Dinerojs represent money in smallest subdivision of the currency i.e. cents
      total = subtract(total, dinero({amount: Math.round(values[i] * factor), currency: selectedCurrency}))
    }

    return toDecimal(total, ({value}) => Number(value))
  }
  return 0
}

//#endregion
